import router from '@/router';
import store from '@/store';
import { isAuthorized, buildConfigWithHeaders } from '@/services/auth.service';
import { removeAuthData } from '@/services/auth.service';
import usersService from '@/services/users.service';
import adminusersService from '@/services/adminusers.service';
import { hasSignedAllDocuments } from '@/utils/hasSIgnedAllDocuments';
import loadExtraData from './loadExtraData';
import moment from 'moment';

const checkYoidService = res => {
    const hasYoidEnabled = res.has_yoid_enabled || false;
    store.dispatch('setHasYoidEnabled', hasYoidEnabled);
};

const setNotificationsRedPoint = res => {
    const intervener = res.private_area.intervener;
    const accounts = res.private_area.accounts;
    hasSignedAllDocuments(intervener, accounts).then(hasDocumentsSigned => {
        store.dispatch('setDocumentsSectionNumber', hasDocumentsSigned);
    });
};

const isMaintenanceMode = res => {
    const maintenanceModeByDB =
        res.private_area_maintenance_mode &&
        String(res.private_area_maintenance_mode).toLowerCase() === 'true'
            ? true
            : false;

    const maintenanceModeByEnv =
        process.env.VUE_APP_MAINTENANCE_MODE &&
        String(process.env.VUE_APP_MAINTENANCE_MODE).toLowerCase() === 'true'
            ? true
            : false;

    return maintenanceModeByDB || maintenanceModeByEnv;
};

const navigateNextRoute = (res, to, next) => {
    if (isMaintenanceMode(res)) {
        // Si está en modo mantenimiento y ya estamos en la ruta de Mantenimineto,
        // cancelamos la redirección a Mantenimiento, porque ya estamos en ella
        if (router.currentRoute?.name === 'MaintenanceMode') {
            store.dispatch('hideLoading');
            return false;
        }

        // Para cualquier otra navegación a una ruta que no sea la de mantenimiento,
        // redirigimos a Mantenimiento
        if (to.name !== 'MaintenanceMode') {
            return router.push({ name: 'MaintenanceMode' });
        }

        next();
    } else {
        // Si no está activado el modo mantenimiento, pero se intenta
        // acceder a la url de mantenimiento, redirigimos a 'Position'
        if (to.name === 'MaintenanceMode') {
            store.dispatch('hideLoading');
            return next({ name: 'Position' });
        }

        const isMinor = moment().diff(res.private_area.intervener.birthdate, 'years') < 18;
        if (
            (!['VALIDATED', 'RD_SENT', 'FINALIZED'].includes(res.private_area.intervener.status) ||
                isMinor) &&
            to.path.toLowerCase() === '/operate'
        ) {
            store.dispatch('hideLoading');
            return next({ name: 'Position' });
        }

        // Si dejo de ser menor y necesita actualizar los datos, los vamos a controlar aquí también
        // para evitar trucos para saltarse esa actualización obligatoria
        if (
            res.private_area.intervener.minor_need_update &&
            to.name.toLowerCase() !== 'login' &&
            to.name.toLowerCase() !== 'updateminor'
        ) {
            return next({ name: 'UpdateMinor' });
        }

        // Si el interviniente necesita renovar sus datos CRS/FATCA, lo forzamos aquí también
        // para evitar saltarse esa actualización obligatoria
        if (
            res.private_area.intervener.needs_crs_fatca_renewal &&
            to.name.toLowerCase() !== 'login' &&
            to.name.toLowerCase() !== 'crsfatcarenewal'
        ) {
            return next({ name: 'CRSFATCARenewal' });
        }

        return next();
    }
};

export default async (to, from, next) => {
    if (to.name === 'logincibersecurity') {
        return next();
    }

    if (
        to.name === 'admin' ||
        to.name === 'login' ||
        to.name === 'passwordRequest' ||
        to.name === 'loginRedirectCobasPlus' ||
        to.name === 'MaintenanceMode' ||
        to.name === 'newPassword'
    ) {
        const biometricAccess = localStorage.getItem('biometricAccess');
        const dismissBiometricAccessTimestamp = localStorage.getItem(
            'dismissBiometricAccessTimestamp'
        );
        localStorage.clear();
        store.dispatch('setDefaultStore');
        if (biometricAccess) {
            localStorage.setItem('biometricAccess', true);
        }
        if (dismissBiometricAccessTimestamp) {
            localStorage.setItem(
                'dismissBiometricAccessTimestamp',
                dismissBiometricAccessTimestamp
            );
        }

        return next();
    }

    // El login ya tiene un loader en el botón de inicio
    // no queda muy bien ponerle este loader por encima
    if (from.name !== 'login') {
        store.dispatch('showLoading');
    }

    if (isAuthorized()) {
        await loadExtraData();

        let config = buildConfigWithHeaders();
        let admin = localStorage.getItem('ADMIN');
        if (admin) {
            let dni = localStorage.getItem('DNI');
            adminusersService
                .getMe(dni, config)
                .then(res => {
                    setNotificationsRedPoint(res);
                    store.dispatch('hideLoading');

                    // La renovación CRS/FATCA también se fuerza cuando entra un administrador
                    // (suplantación), para que no pueda saltársela navegando a otra sección
                    if (
                        res.private_area.intervener.needs_crs_fatca_renewal &&
                        to.name.toLowerCase() !== 'login' &&
                        to.name.toLowerCase() !== 'crsfatcarenewal'
                    ) {
                        return next({ name: 'CRSFATCARenewal' });
                    }

                    return next();
                })
                .catch(() => {
                    removeAuthData().then(() => {
                        if (!isAuthorized()) next({ name: 'login' });
                        else next();
                    });
                });
        } else {
            usersService
                .getMe(config)
                .then(res => {
                    store.dispatch('setIntervener', res.private_area.intervener);

                    // Si por alguna razón, queremos deshabilitarle el acceso biométrico desde
                    // la base de datos, y el usuario tiene activado el acceso biométrico, en la app
                    // está guardado en localStorage 'biometricAccess' a true, que sirve para mostrar
                    // el acceso biométrico en el login. Si en el 'me' nos llega que el usuario no lo
                    // tiene activado, pero en el localStorage si lo está, enctonces lo borramos de la storage
                    if (
                        !res.private_area.intervener.has_app_enable_biometric_access &&
                        localStorage.getItem('biometricAccess')
                    ) {
                        localStorage.removeItem('biometricAccess');
                    }

                    setNotificationsRedPoint(res);
                    navigateNextRoute(res, to, next);
                    checkYoidService(res);
                })
                .catch(() => {
                    removeAuthData().then(() => {
                        if (!isAuthorized()) next({ name: 'login' });
                        else next();
                    });
                });
        }
    } else {
        const redirectPath = to.path.startsWith('/') ? to.path.substring(1) : to.path;
        if (redirectPath) {
            return next('/login?redirect=' + redirectPath);
        } else {
            return next({ name: 'login' });
        }
    }
};
